import type { OperationEvent, OperationsSnapshot, TaskStatus, WorkClassification } from "../domain/operations";

export interface NewWorkInput {
  title: string;
  eventId: string;
  classification: WorkClassification;
}

const IMPORTED_EVENTS_KEY = "fswl-imported-events-v1";

function loadPersistedImportEvents(): OperationEvent[] {
  try {
    const raw = localStorage.getItem(IMPORTED_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as OperationEvent[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistImportEvent(event: OperationEvent): void {
  const existing = loadPersistedImportEvents().filter((entry) => entry.id !== event.id);
  localStorage.setItem(IMPORTED_EVENTS_KEY, JSON.stringify([...existing, event]));
}

export function mergeImportedEvents(snapshot: OperationsSnapshot): OperationsSnapshot {
  const imported = loadPersistedImportEvents();
  if (imported.length === 0) return snapshot;
  const existingIds = new Set(snapshot.events.map((event) => event.id));
  const merged = [...snapshot.events];
  for (const event of imported) {
    if (!existingIds.has(event.id)) merged.push(event);
  }
  return { ...snapshot, events: merged };
}

export interface OperationsRepository {
  getSnapshot(): Promise<OperationsSnapshot>;
  setTaskStatus(taskId: string, status: TaskStatus, blockerNote?: string): Promise<OperationsSnapshot>;
  resolveAttention(attentionId: string): Promise<OperationsSnapshot>;
  addWork(input: NewWorkInput): Promise<OperationsSnapshot>;
  addEvent(event: OperationEvent): Promise<OperationsSnapshot>;
  addBackendEvent(event: OperationEvent): Promise<OperationsSnapshot>;
}

export class InMemoryOperationsRepository implements OperationsRepository {
  constructor(private snapshot: OperationsSnapshot) {}

  async getSnapshot(): Promise<OperationsSnapshot> {
    return structuredClone(this.snapshot);
  }

  async setTaskStatus(taskId: string, status: TaskStatus, blockerNote?: string): Promise<OperationsSnapshot> {
    this.snapshot = {
      ...this.snapshot,
      tasks: this.snapshot.tasks.map((task) => task.id === taskId
        ? { ...task, status, blockerNote: status === "blocked" ? (blockerNote || task.blockerNote || "Flagged blocked") : undefined }
        : task),
    };
    return this.getSnapshot();
  }

  async resolveAttention(attentionId: string): Promise<OperationsSnapshot> {
    this.snapshot = {
      ...this.snapshot,
      attention: this.snapshot.attention.map((item) => item.id === attentionId ? { ...item, resolved: true } : item),
    };
    return this.getSnapshot();
  }

  async addWork(input: NewWorkInput): Promise<OperationsSnapshot> {
    const id = `work-${Date.now()}`;
    const task = {
      id,
      blockId: "kitchen-am",
      detail: "Location to be confirmed",
      status: "eligible" as const,
      ...input,
    };
    this.snapshot = {
      ...this.snapshot,
      tasks: [...this.snapshot.tasks, task],
      blocks: this.snapshot.blocks.map((block) => block.id === "kitchen-am" ? { ...block, taskIds: [...block.taskIds, id] } : block),
    };
    return this.getSnapshot();
  }

  async addEvent(event: OperationEvent): Promise<OperationsSnapshot> {
    if (this.snapshot.events.some((entry) => entry.id === event.id)) {
      throw new Error(`Event already exists: ${event.id}`);
    }
    this.snapshot = {
      ...this.snapshot,
      events: [...this.snapshot.events, event],
    };
    if (event.sourceInvoice) {
      persistImportEvent(event);
    }
    return this.getSnapshot();
  }

  async addBackendEvent(event: OperationEvent): Promise<OperationsSnapshot> {
    if (this.snapshot.events.some((entry) => entry.id === event.id)) {
      return this.getSnapshot();
    }
    this.snapshot = {
      ...this.snapshot,
      events: [...this.snapshot.events, event],
    };
    return this.getSnapshot();
  }
}
